/**
 * F 392 — InlineDatabase: a database embedded INSIDE a page.
 *
 * Before this, the only way to see a database in FCC was the full-page surface
 * (`DatabaseView`, routed from a page of type 'database'). FVC needs a database
 * nested inside a record, Notion-style. This block is that surface — it reads
 * the SAME storage the full page reads (`loadDatabase`), renders a compact
 * read-only preview, and offers a button that hands off to the real surface
 * rather than reimplementing it.
 *
 * Deliberately READ-ONLY inline. Editing here would mean two writers for one
 * database (this block and DatabaseView) with no conflict resolution between
 * them. "Open full page" is the single write path, and that is a design
 * decision, not a missing feature.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { loadDatabase } from '../../../core/workspace/databaseStorage';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import type { Database, DatabaseItem } from '../../../types/models/Database';

/** How many rows an inline block shows before it truncates. */
export const INLINE_PREVIEW_LIMIT = 8;

interface InlineDatabaseProps {
  databaseId: string;
}

/**
 * Resolve a database + its rows, tolerating an id that no longer resolves.
 *
 * `loadDatabase` returns undefined for an unknown id — which is the NORMAL
 * state for a block pasted from another workspace, or after the database was
 * deleted. That has to render as a visible message, not a crash: a page whose
 * content block throws takes down the whole page editor.
 */
function useInlineDatabase(databaseId: string) {
  const [db, setDb] = useState<Database | undefined>(undefined);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // RemoteState may hydrate asynchronously on boot, so re-read on a short
    // interval until the database appears. This is the same reason SidebarV2
    // re-renders on the `database:refresh` event.
    const read = () => {
      const found = databaseId ? loadDatabase(databaseId) : undefined;
      if (cancelled) return;
      if (found) {
        setDb(found);
        setMissing(false);
      } else if (!missing) {
        setMissing(true);
      }
    };
    read();
    const id = window.setInterval(read, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // `missing` is deliberately out of deps: it is the "stop alarming" latch,
    // and including it would restart the interval every time it flipped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databaseId]);

  const rows = useMemo<DatabaseItem[]>(() => {
    const items = db?.items ?? [];
    return items.slice(0, INLINE_PREVIEW_LIMIT);
  }, [db]);

  const total = db?.items?.length ?? 0;

  return { db, rows, total, missing };
}

/** Read a checkbox/text value for the first non-empty cell, for the row summary. */
function firstCellText(item: DatabaseItem, props: Database['schema']['properties']): string {
  for (const prop of props ?? []) {
    if (prop.id === 'title') continue;
    const value = item.properties?.find((p) => p.propertyId === prop.id)?.value;
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'boolean') return value ? 'yes' : 'no';
  }
  return '';
}

export function InlineDatabase({ databaseId }: InlineDatabaseProps) {
  const workspace = useWorkspace();
  const { db, rows, total, missing } = useInlineDatabase(databaseId);

  const properties = db?.schema?.properties ?? [];
  const hasMore = total > rows.length;

  if (missing) {
    return (
      <div
        className="fc-inline-database fc-inline-database--empty"
        data-testid="fc-inline-database"
        data-database-id={databaseId}
        data-state="missing"
      >
        <span className="fc-inline-database__empty-icon" aria-hidden="true">📊</span>
        <span className="fc-inline-database__empty-text">
          No database with id <code>{databaseId || '(none)'}</code>
        </span>
      </div>
    );
  }

  if (!db) {
    // Present in the document, but RemoteState has not hydrated it yet.
    return (
      <div
        className="fc-inline-database fc-inline-database--loading"
        data-testid="fc-inline-database"
        data-database-id={databaseId}
        data-state="loading"
      >
        <span className="fc-inline-database__empty-text">Loading database…</span>
      </div>
    );
  }

  return (
    <div
      className="fc-inline-database"
      data-testid="fc-inline-database"
      data-database-id={databaseId}
      data-state="ready"
      data-item-count={total}
    >
      <header className="fc-inline-database__header">
        <span className="fc-inline-database__icon" aria-hidden="true">📊</span>
        <span className="fc-inline-database__name" data-testid="fc-inline-database-name">
          {db.name}
        </span>
        <span className="fc-inline-database__count">
          {total} {total === 1 ? 'item' : 'items'}
        </span>
        <button
          type="button"
          className="fc-inline-database__open"
          data-testid="fc-inline-database-open"
          onClick={() => {
            // The page id DOUBLES as the database id (WorkspaceContext.createPage),
            // so this is the same call the sidebar row makes.
            void workspace?.selectPage?.(databaseId);
          }}
        >
          Open full page →
        </button>
      </header>

      {total === 0 ? (
        <div className="fc-inline-database__empty">
          <span className="fc-inline-database__empty-text">This database has no records.</span>
        </div>
      ) : (
        <div className="fc-inline-database__table" role="table" data-testid="fc-inline-database-table">
          {rows.map((item) => (
            <div className="fc-inline-database__row" role="row" key={item.id} data-item-id={item.id}>
              <span className="fc-inline-database__cell fc-inline-database__cell--title" role="cell">
                {item.title}
              </span>
              <span className="fc-inline-database__cell fc-inline-database__cell--meta" role="cell">
                {firstCellText(item, properties)}
              </span>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <footer className="fc-inline-database__footer">
          Showing {rows.length} of {total} — open the full page for all records.
        </footer>
      )}
    </div>
  );
}

export default InlineDatabase;